
import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { 
  Search, 
  Plus, 
  Filter, 
  RefreshCw, 
  ChevronUp, 
  ChevronDown,
  BringToFront,
  Lightbulb,
  DollarSign,
  Calendar
} from "lucide-react";

// Sample data for recipe innovation
const trendingIngredients = [
  { name: 'Gofio Canario', trend: 94, costImpact: 'Medium', availability: 'High', seasonality: 'Year-round' },
  { name: 'Papas Negras', trend: 82, costImpact: 'Low', availability: 'High', seasonality: 'Year-round' },
  { name: 'Algas Marinas', trend: 78, costImpact: 'Low', availability: 'Medium', seasonality: 'Year-round' },
  { name: 'Miel de Palma', trend: 76, costImpact: 'Medium', availability: 'High', seasonality: 'Year-round' },
  { name: 'Lapas', trend: 71, costImpact: 'Medium', availability: 'Medium', seasonality: 'Year-round' },
  { name: 'Calabazas de Lanzarote', trend: 68, costImpact: 'Medium', availability: 'Seasonal', seasonality: 'Spring/Summer' },
  { name: 'Pescado de Cercanía', trend: 67, costImpact: 'High', availability: 'Medium', seasonality: 'Seasonal' },
];

const recipeIdeas = [
  { 
    id: 1, 
    name: 'Bowl de Gofio con Verduras Asadas', 
    ingredients: ['Gofio', 'Millo', 'Batatas Asadas', 'Kale', 'Mojo Verde'],
    cuisineMatch: 'Canaria/Mediterránea',
    profitMargin: 68,
    complexity: 'Medium'
  },
  { 
    id: 2, 
    name: 'Arroz con Mojo y Algas', 
    ingredients: ['Algas', 'Arroz Integral', 'Proteína Vegetal', 'Mojo Picón', 'Cebolletas'],
    cuisineMatch: 'Canaria Fusión',
    profitMargin: 72,
    complexity: 'Low'
  },
  { 
    id: 3, 
    name: 'Tacos de Vieja con Ensalada Fermentada', 
    ingredients: ['Vieja Fresca', 'Tortillas de Maíz', 'Col Fermentada', 'Crema de Aguacate'],
    cuisineMatch: 'Canaria-Mexicana Fusión',
    profitMargin: 65,
    complexity: 'Medium'
  },
  { 
    id: 4, 
    name: 'Torta de Gofio con Tomates Antiguos', 
    ingredients: ['Harina de Gofio', 'Tomates Antiguos', 'Queso Vegetal', 'Hierbas Frescas'],
    cuisineMatch: 'Canaria',
    profitMargin: 70,
    complexity: 'Medium'
  },
];

const menuGapAnalysis = [
  { 
    category: 'Platos Vegetales', 
    competitorCoverage: 85, 
    yourCoverage: 40,
    gapScore: 45,
    opportunity: 'High',
    recommendedItems: ['Hamburguesa Vegetal', 'Bistec de Coliflor', 'Wellington de Setas']
  },
  { 
    category: 'Opciones Sin Gluten', 
    competitorCoverage: 75, 
    yourCoverage: 60,
    gapScore: 15,
    opportunity: 'Medium',
    recommendedItems: ['Bowl de Gofio', 'Plato de Proteínas', 'Verduras Rellenas']
  },
  { 
    category: 'Pescado Sostenible', 
    competitorCoverage: 65, 
    yourCoverage: 25,
    gapScore: 40,
    opportunity: 'High',
    recommendedItems: ['Tacos de Pescado Local', 'Ensalada de Algas', 'Guiso de Mariscos Sostenible']
  },
  { 
    category: 'Alimentos Fermentados', 
    competitorCoverage: 50, 
    yourCoverage: 15,
    gapScore: 35,
    opportunity: 'Medium',
    recommendedItems: ['Bowl de Algas Fermentadas', 'Proteínas con Glaseado de Miso', 'Marinadas de Kombucha']
  },
];

const foodCostAnalysis = [
  { 
    recipe: 'Bowl de Gofio', 
    ingredients: 10, 
    rawCost: 4.85,
    labor: 2.25,
    totalCost: 7.10,
    suggestedPrice: 18.95,
    margin: 62.5
  },
  { 
    recipe: 'Arroz con Mojo', 
    ingredients: 8, 
    rawCost: 3.75,
    labor: 1.95,
    totalCost: 5.70,
    suggestedPrice: 16.95,
    margin: 66.4
  },
  { 
    recipe: 'Tacos de Vieja', 
    ingredients: 9, 
    rawCost: 6.25,
    labor: 2.50,
    totalCost: 8.75,
    suggestedPrice: 21.95,
    margin: 60.1
  },
];

const launchTiming = [
  { 
    item: 'Bowl de Gofio', 
    bestMonth: 'Enero', 
    reasoning: 'Enfoque en salud por Año Nuevo',
    competingEvents: 'Ninguno',
    estimatedDemand: 'Alta',
    marketingOpportunity: 'Campañas de salud de Año Nuevo'
  },
  { 
    item: 'Arroz con Mojo', 
    bestMonth: 'Marzo', 
    reasoning: 'Comida reconfortante de temporada transitoria',
    competingEvents: 'Carnaval de Canarias',
    estimatedDemand: 'Media',
    marketingOpportunity: 'Exploración de sabores locales'
  },
  { 
    item: 'Tacos de Vieja', 
    bestMonth: 'Junio', 
    reasoning: 'Temporada de pescado de verano',
    competingEvents: 'Temporada de parrilladas',
    estimatedDemand: 'Alta',
    marketingOpportunity: 'Vínculos con conservación del océano'
  },
  { 
    item: 'Torta de Gofio con Tomates', 
    bestMonth: 'Agosto', 
    reasoning: 'Temporada alta de tomates locales',
    competingEvents: 'Promociones de Mercados Agrícolas',
    estimatedDemand: 'Muy Alta',
    marketingOpportunity: 'Asociaciones con granjas locales'
  },
];

// Radar chart data
const radarData = [
  {
    subject: 'Innovación',
    A: 85,
    B: 65,
    fullMark: 100,
  },
  {
    subject: 'Coste',
    A: 70,
    B: 60,
    fullMark: 100,
  },
  {
    subject: 'Alineación con Tendencias',
    A: 90,
    B: 55,
    fullMark: 100,
  },
  {
    subject: 'Ejecución',
    A: 65,
    B: 50,
    fullMark: 100,
  },
  {
    subject: 'Atractivo para Clientes',
    A: 95,
    B: 70,
    fullMark: 100,
  },
];

const RecipeInnovation = () => {
  const [searchTerm, setSearchTerm] = useState("");
  
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-3xl font-semibold tracking-tight">Recipe Innovation</h1>
        
        <div className="flex flex-wrap gap-3 w-full md:w-auto">
          <div className="relative w-full md:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search ingredients, recipes..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <Button variant="outline" className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </Button>
          
          <Button className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            New Recipe
          </Button>
        </div>
      </div>

      <Tabs defaultValue="trending" className="w-full">
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="trending">Trending Ingredients</TabsTrigger>
          <TabsTrigger value="ideas">Recipe Ideas</TabsTrigger>
          <TabsTrigger value="gaps">Menu Gaps</TabsTrigger>
          <TabsTrigger value="cost">Food Cost Analysis</TabsTrigger>
          <TabsTrigger value="timing">Launch Timing</TabsTrigger>
        </TabsList>
        
        <TabsContent value="trending" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Trending Ingredients Analysis</CardTitle>
              <CardDescription>
                Emerging ingredients with market trends and cost impact
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="col-span-1 md:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Ingredient Trend Scores</CardTitle>
                  </CardHeader>
                  <CardContent className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={trendingIngredients}
                        margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="name" 
                          angle={-45}
                          textAnchor="end"
                          height={70}
                        />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="trend" name="Trend Score" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Cost Impact Distribution</CardTitle>
                  </CardHeader>
                  <CardContent className="h-80 flex items-center justify-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Low', value: 2 },
                            { name: 'Medium', value: 4 },
                            { name: 'High', value: 1 },
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          <Cell fill="#4ade80" />
                          <Cell fill="#facc15" />
                          <Cell fill="#f87171" />
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>
              
              <div className="mt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ingredient</TableHead>
                      <TableHead>Trend Score</TableHead>
                      <TableHead>Cost Impact</TableHead>
                      <TableHead>Availability</TableHead>
                      <TableHead>Seasonality</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trendingIngredients.map((ingredient, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{ingredient.name}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{ingredient.trend}</span>
                            <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary"
                                style={{ width: `${ingredient.trend}%` }}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            ingredient.costImpact === 'Low' 
                              ? 'bg-green-100 text-green-800 hover:bg-green-100' 
                              : ingredient.costImpact === 'Medium'
                                ? 'bg-amber-100 text-amber-800 hover:bg-amber-100'
                                : 'bg-red-100 text-red-800 hover:bg-red-100'
                          }>
                            {ingredient.costImpact}
                          </Badge>
                        </TableCell>
                        <TableCell>{ingredient.availability}</TableCell>
                        <TableCell>{ingredient.seasonality}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="ideas" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Recipe Innovation Ideas</CardTitle>
              <CardDescription>
                New recipe concepts based on trend analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="col-span-1 md:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Recipe Profitability Comparison</CardTitle>
                  </CardHeader>
                  <CardContent className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart outerRadius={90} width={730} height={250} data={radarData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="subject" />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} />
                        <Radar name="New Recipes" dataKey="A" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                        <Radar name="Current Menu" dataKey="B" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} />
                        <Legend />
                      </RadarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                
                <div className="space-y-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex justify-between items-center">
                        <CardTitle className="text-base">Top Recipe Idea</CardTitle>
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">72% Margin</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <h3 className="font-medium text-lg">Kimchi Fried Rice with Plant-Based Protein</h3>
                      <p className="text-sm text-muted-foreground mt-1">Korean Fusion</p>
                      <div className="flex flex-wrap gap-1 mt-3">
                        {['Kimchi', 'Brown Rice', 'Plant-Based Protein', 'Gochujang', 'Green Onions'].map((ing, i) => (
                          <Badge key={i} variant="outline">{ing}</Badge>
                        ))}
                      </div>
                      <div className="flex justify-between items-center mt-4">
                        <div className="flex items-center">
                          <DollarSign className="h-4 w-4 mr-1 text-muted-foreground" />
                          <span className="text-sm">Low Cost</span>
                        </div>
                        <span className="text-sm text-muted-foreground">Complexity: Low</span>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Button className="w-full">View All Recipe Ideas</Button>
                </div>
              </div>
              
              <div className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium">Recipe Concepts</h3>
                  <div className="relative w-64">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="search"
                      placeholder="Search recipes..."
                      className="pl-8"
                    />
                  </div>
                </div>
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Recipe Name</TableHead>
                      <TableHead>Cuisine Match</TableHead>
                      <TableHead>Profit Margin</TableHead>
                      <TableHead>Complexity</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recipeIdeas.map((recipe) => (
                      <TableRow key={recipe.id}>
                        <TableCell className="font-medium">{recipe.name}</TableCell>
                        <TableCell>{recipe.cuisineMatch}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{recipe.profitMargin}%</span>
                            <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div 
                                className={`h-full ${
                                  recipe.profitMargin > 70 
                                    ? "bg-green-500" 
                                    : recipe.profitMargin > 65 
                                      ? "bg-amber-500" 
                                      : "bg-orange-500"
                                }`}
                                style={{ width: `${recipe.profitMargin}%` }}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{recipe.complexity}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="outline" size="sm">Details</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="gaps" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Menu Gap Analysis</CardTitle>
              <CardDescription>
                Identifying opportunities based on competitor analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Coverage Comparison</CardTitle>
                  </CardHeader>
                  <CardContent className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={menuGapAnalysis}
                        margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                        layout="vertical"
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis 
                          dataKey="category" 
                          type="category"
                          width={150}
                        />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="competitorCoverage" name="Competitor Coverage" fill="#94a3b8" />
                        <Bar dataKey="yourCoverage" name="Your Coverage" fill="#3b82f6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Gap Analysis Form</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="category">Category</Label>
                          <Input id="category" placeholder="e.g., Plant-Based Entrees" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="gap-score">Gap Score</Label>
                          <Input id="gap-score" type="number" placeholder="Gap percentage" />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="competitor">Competitor Coverage</Label>
                          <Input id="competitor" type="number" placeholder="0-100%" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="your-coverage">Your Coverage</Label>
                          <Input id="your-coverage" type="number" placeholder="0-100%" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="opportunity">Opportunity</Label>
                          <Input id="opportunity" placeholder="High/Medium/Low" />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="item1">Recommended Item 1</Label>
                          <Input id="item1" placeholder="Menu item" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="item2">Recommended Item 2</Label>
                          <Input id="item2" placeholder="Menu item" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="item3">Recommended Item 3</Label>
                          <Input id="item3" placeholder="Menu item" />
                        </div>
                      </div>
                      
                      <Button className="w-full">Add Gap Analysis</Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
              
              <div className="relative w-full mb-4">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="search"
                  placeholder="Search categories..."
                  className="pl-8"
                />
              </div>
              
              <div className="rounded-lg border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Category</TableHead>
                      <TableHead>Competitor</TableHead>
                      <TableHead>Your Coverage</TableHead>
                      <TableHead>Gap Score</TableHead>
                      <TableHead className="text-right">Opportunity</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {menuGapAnalysis.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{item.category}</TableCell>
                        <TableCell>{item.competitorCoverage}%</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{item.yourCoverage}%</span>
                            <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary"
                                style={{ width: `${item.yourCoverage}%` }}
                              />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-red-50 text-red-800 border-red-200">
                            {item.gapScore}% Gap
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge className={
                            item.opportunity === 'High' 
                              ? 'bg-green-100 text-green-800 hover:bg-green-100'
                              : 'bg-amber-100 text-amber-800 hover:bg-amber-100'
                          }>
                            {item.opportunity}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="cost" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Food Cost Analysis</CardTitle>
              <CardDescription>
                Theoretical food costs and pricing recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <Card className="col-span-1 md:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Cost Breakdown by Recipe</CardTitle>
                  </CardHeader>
                  <CardContent className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={foodCostAnalysis}
                        margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="recipe" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="rawCost" name="Raw Cost" stackId="a" fill="#3b82f6" />
                        <Bar dataKey="labor" name="Labor Cost" stackId="a" fill="#93c5fd" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Margin Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Average Food Cost %</span>
                          <span className="font-medium">36.2%</span>
                        </div>
                        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-500"
                            style={{ width: '36.2%' }}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Average Profit Margin</span>
                          <span className="font-medium">63.8%</span>
                        </div>
                        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary"
                            style={{ width: '63.8%' }}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Target Margin</span>
                          <span className="font-medium">65.0%</span>
                        </div>
                        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-amber-500"
                            style={{ width: '65%' }}
                          />
                        </div>
                      </div>
                      
                      <div className="pt-4 border-t">
                        <div className="flex justify-between items-center">
                          <span className="font-medium">Margin Status:</span>
                          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                            Near Target
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipe</TableHead>
                    <TableHead>Raw Cost</TableHead>
                    <TableHead>Labor Cost</TableHead>
                    <TableHead>Total Cost</TableHead>
                    <TableHead className="text-right">Suggested Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {foodCostAnalysis.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.recipe}</TableCell>
                      <TableCell>${item.rawCost.toFixed(2)}</TableCell>
                      <TableCell>${item.labor.toFixed(2)}</TableCell>
                      <TableCell>${item.totalCost.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        <div className="space-y-1">
                          <div className="font-medium">${item.suggestedPrice.toFixed(2)}</div>
                          <div className="text-xs text-muted-foreground">({item.margin}% margin)</div>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="timing" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Launch Timing Strategy</CardTitle>
              <CardDescription>
                Strategic timing recommendations for menu launches
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <Card className="col-span-1 md:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Launch Calendar</CardTitle>
                  </CardHeader>
                  <CardContent className="h-96 flex items-center justify-center">
                    <Calendar className="h-80 w-80" />
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Launch Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="p-3 bg-green-50 border border-green-100 rounded-lg">
                        <h4 className="font-medium">January Launch</h4>
                        <p className="text-sm mt-1">Ancient Grain Bowl</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Perfect timing to capitalize on New Year health resolutions
                        </p>
                      </div>
                      
                      <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                        <h4 className="font-medium">June Launch</h4>
                        <p className="text-sm mt-1">Sustainable Fish Tacos</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Aligns with summer seafood season and sustainability focus
                        </p>
                      </div>
                      
                      <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg">
                        <h4 className="font-medium">August Launch</h4>
                        <p className="text-sm mt-1">Heirloom Tomato Flatbread</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Peak season for heirloom tomatoes with highest quality
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Best Month</TableHead>
                    <TableHead>Reasoning</TableHead>
                    <TableHead>Demand</TableHead>
                    <TableHead className="text-right">Marketing</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {launchTiming.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.item}</TableCell>
                      <TableCell>{item.bestMonth}</TableCell>
                      <TableCell>
                        <span className="text-sm">{item.reasoning}</span>
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          item.estimatedDemand === 'High' || item.estimatedDemand === 'Very High'
                            ? 'bg-green-100 text-green-800 hover:bg-green-100'
                            : 'bg-amber-100 text-amber-800 hover:bg-amber-100'
                        }>
                          {item.estimatedDemand}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="outline" size="sm">View Plan</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RecipeInnovation;
